import { useCallback, useEffect, useRef, useState } from "react";
import { authStorage } from "../lib/storage/authStorage";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { recommendationsService } from "../features/recommendations/service";
import { useInteractionTracking } from "../features/recommendations/useInteractionTracking";
import { useScrollTracking } from "../features/analytics/useTracking";
import type { ScoredFeed } from "../features/recommendations/types";
import { env } from "../config/env";
import { currencySymbol } from "../lib/currency";

// ── Helpers ────────────────────────────────────────────────────────────────────

function resolveImage(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("/uploads/")) return `${env.apiBaseUrl}${url}`;
  return url;
}

function feedImage(feed: Record<string, unknown>): string | undefined {
  const media = feed.media as Record<string, unknown> | undefined;
  return resolveImage(
    (media?.image_clean as string) ||
    (feed.image_clean as string) ||
    (feed.ai_generated_flyer as string) ||
    undefined,
  );
}

function feedTitle(feed: Record<string, unknown>): string {
  return (feed.title as string) || "Untitled Deal";
}

function feedId(feed: Record<string, unknown>): string {
  return (feed._id as string) || (feed.id as string) || "";
}

function feedLocation(feed: Record<string, unknown>): string {
  const dest = feed.destination as Record<string, unknown> | undefined;
  const countries: string[] = (dest?.countries as string[]) || (feed.countries as string[]) || [];
  const cities: string[] = (dest?.cities as string[]) || (feed.cities as string[]) || [];
  if (countries.length && cities.length) return `${countries[0]} · ${cities[0]}`;
  if (countries.length) return countries[0];
  if (cities.length) return cities[0];
  return "";
}

function feedPrice(feed: Record<string, unknown>): string | null {
  const pricing = feed.pricing as Record<string, unknown> | undefined;
  const discounted = pricing?.discounted_price ?? feed.discounted_price ?? feed.price_min;
  const original = pricing?.original_price ?? feed.original_price ?? feed.price_max;
  if (!discounted && !original) return null;
  // feed_metadata docs carry a top-level `currency` (and a `pricing.currency`
  // mirror); older docs written before that have neither and fall back to INR.
  const sym = currencySymbol(
    (pricing?.currency as string | undefined) || (feed.currency as string | undefined),
  );
  if (discounted) return `${sym}${Number(discounted).toLocaleString()}`;
  return `${sym}${Number(original).toLocaleString()}`;
}

function feedTags(feed: Record<string, unknown>): string[] {
  return (feed.tags as string[]) || [];
}

function scoreColor(score: number): string {
  if (score >= 150) return "bg-emerald-500";
  if (score >= 100) return "bg-blue-500";
  if (score >= 60) return "bg-amber-500";
  return "bg-gray-400";
}

// ── IntersectionObserver-based view tracker ────────────────────────────────────

function useFeedCardRef(feedId: string, onVisible: (id: string) => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || fired.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !fired.current) {
          fired.current = true;
          onVisible(feedId);
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [feedId, onVisible]);

  return ref;
}

// ── Feed Card ──────────────────────────────────────────────────────────────────

interface FeedCardProps {
  item: ScoredFeed;
  onVisible: (id: string) => void;
  onClick: (item: ScoredFeed) => void;
}

function FeedCard({ item, onVisible, onClick }: FeedCardProps) {
  const feed = item.feed as Record<string, unknown>;
  const id = feedId(feed);
  const ref = useFeedCardRef(id, onVisible);
  const img = feedImage(feed);
  const location = feedLocation(feed);
  const price = feedPrice(feed);
  const tags = feedTags(feed).slice(0, 3);

  return (
    <div
      ref={ref}
      onClick={() => onClick(item)}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden group"
    >
      {/* Image */}
      <div className="relative h-44 bg-gradient-to-br from-blue-100 to-purple-100 overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={feedTitle(feed)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
            </svg>
          </div>
        )}

        {/* Score badge */}
        <div className={`absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full text-white text-xs font-bold ${scoreColor(item.score)}`}>
          {Math.round(item.score)}
        </div>

        {/* For You pill */}
        <div className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full bg-black/40 text-white text-xs font-medium backdrop-blur-sm">
          For You
        </div>
        {/* Temp debug badge — remove after rec engine testing */}
        {item._rec_source && (
          <div className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded bg-purple-700/90 text-white text-[10px] font-mono">
            ⚡ REC ENGINE
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3.5">
        <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 mb-1.5">
          {feedTitle(feed)}
        </h3>

        {location && (
          <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
            <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {location}
          </p>
        )}

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {tags.map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-xs border border-blue-100">
                {tag}
              </span>
            ))}
          </div>
        )}

        {price && (
          <p className="text-sm font-bold text-gray-900">{price}</p>
        )}
      </div>
    </div>
  );
}

// ── Deal Detail Modal ──────────────────────────────────────────────────────────

function FeedModal({
  item,
  onClose,
  onSave,
  onLike,
  onEnquiry,
}: {
  item: ScoredFeed;
  onClose: () => void;
  onSave: (id: string) => void;
  onLike: (id: string) => void;
  onEnquiry: (id: string) => void;
}) {
  const feed = item.feed as Record<string, unknown>;
  const id = feedId(feed);
  const img = feedImage(feed);
  const location = feedLocation(feed);
  const price = feedPrice(feed);
  const tags = feedTags(feed) as string[];
  const desc = (feed.description as string) || "";
  const pricing = feed.pricing as Record<string, unknown> | undefined;
  const original = (pricing?.original_price ?? feed.original_price) as string | undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Image */}
        {img && (
          <div className="h-52 overflow-hidden rounded-t-2xl">
            <img src={img} alt={feedTitle(feed)} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="p-5 space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg font-bold text-gray-900 leading-snug">{feedTitle(feed)}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Score + Location */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-white text-xs font-bold ${scoreColor(item.score)}`}>
              Match score: {Math.round(item.score)}
            </span>
            {location && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                {location}
              </span>
            )}
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs border border-blue-100">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          {desc && <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>}

          {/* Pricing */}
          {(price || original) && (
            <div className="flex items-baseline gap-2">
              {price && <span className="text-xl font-bold text-gray-900">{price}</span>}
              {original && price && String(original) !== String(price).replace(/[₹$€]/g, "") && (
                <span className="text-sm text-gray-400 line-through">
                  ₹{Number(original).toLocaleString()}
                </span>
              )}
              <span className="text-xs text-gray-500">per person</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => { onEnquiry(id); }}
              className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Enquire Now
            </button>
            <button
              onClick={() => { onSave(id); }}
              className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              title="Save for later"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </button>
            <button
              onClick={() => { onLike(id); }}
              className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              title="Like"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

const RecommendedFeedPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAuthReady } = useAuth();
  const [feeds, setFeeds] = useState<ScoredFeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noPrefs, setNoPrefs] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ScoredFeed | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 20;

  // Seller lead inbox (rec engine routed leads) — temp debug, remove after testing
  const [sellerLeads, setSellerLeads] = useState<any[]>([]);
  const [sellerLeadsLoading, setSellerLeadsLoading] = useState(true);
  const sellerLeadsWsRef = useRef<WebSocket | null>(null);

  const { recordView, recordClick, recordSave, recordLike, recordEnquiry } =
    useInteractionTracking();
  useScrollTracking("/feed");

  const loadFeeds = useCallback(async (offset: number, replace: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const result = await recommendationsService.getRecommendedFeeds(PAGE_SIZE, offset);
      if (result.message) {
        setNoPrefs(true);
        setLoading(false);
        return;
      }
      const items = result.feeds ?? [];
      setFeeds((prev) => (replace ? items : [...prev, ...items]));
      setHasMore(items.length === PAGE_SIZE);
    } catch {
      setError("Could not load your recommendations. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    // Anonymous visitors may browse the feed shell; personalised recommendations
    // require an account, so we surface the sign-in prompt instead of redirecting.
    if (!isAuthenticated) {
      setNoPrefs(true);
      setLoading(false);
      return;
    }
    void loadFeeds(0, true);
  }, [isAuthReady, isAuthenticated, loadFeeds]);

  // Fetch rec-engine routed leads for sellers — temp debug, remove after testing
  const fetchSellerLeads = useCallback(() => {
    if ((user as any)?.role !== "seller") return;
    setSellerLeadsLoading(true);
    recommendationsService
      .getSellerLeadInbox()
      .then((data) => setSellerLeads(data.leads || []))
      .catch(() => {})
      .finally(() => setSellerLeadsLoading(false));
  }, [user]);

  useEffect(() => {
    if (!isAuthReady || !isAuthenticated || (user as any)?.role !== "seller") {
      setSellerLeadsLoading(false);
      return;
    }
    fetchSellerLeads();
  }, [isAuthReady, isAuthenticated, user, fetchSellerLeads]);

  // WebSocket: auto-refresh leads when rec engine routes a new lead to this seller
  useEffect(() => {
    if (!isAuthReady || !isAuthenticated || (user as any)?.role !== "seller") return;
    const sellerId = (user as any)?._id || (user as any)?.id;
    const token = authStorage.getToken();
    if (!sellerId || !token) return;

    const recBase = (env.recApiBaseUrl || "http://localhost:8003")
      .replace(/^https/, "wss")
      .replace(/^http/, "ws");
    const ws = new WebSocket(`${recBase}/ws/leads/${sellerId}?token=${token}`);
    sellerLeadsWsRef.current = ws;

    ws.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data);
        if (event.type === "lead_routed") {
          fetchSellerLeads();
        }
      } catch { /* ignore malformed */ }
    };
    ws.onerror = () => {};

    return () => {
      ws.close();
      sellerLeadsWsRef.current = null;
    };
  }, [isAuthReady, isAuthenticated, user, fetchSellerLeads]);

  const handleVisible = useCallback(
    (id: string) => recordView(id),
    [recordView],
  );

  const handleCardClick = useCallback(
    (item: ScoredFeed) => {
      const id = feedId(item.feed as Record<string, unknown>);
      recordClick(id);
      setSelectedItem(item);
    },
    [recordClick],
  );

  const handleSave = useCallback(
    (id: string) => {
      recordSave(id);
    },
    [recordSave],
  );

  const handleLike = useCallback(
    (id: string) => {
      recordLike(id);
    },
    [recordLike],
  );

  const handleEnquiry = useCallback(
    (id: string) => {
      recordEnquiry(id);
    },
    [recordEnquiry],
  );

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    void loadFeeds(next * PAGE_SIZE, false);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="page-shell">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">For You</h1>
            <p className="text-xs text-gray-500">Personalised for {user?.name || "you"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setPage(0); void loadFeeds(0, true); }}
              className="text-xs text-blue-600 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50 transition-colors"
            >
              Refresh
            </button>
            <button
              onClick={() => navigate("/profile")}
              className="text-xs text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
            >
              Edit Preferences
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* No preferences state */}
        {noPrefs && !loading && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 13l4.553 2.276A1 1 0 0021 21.382V10.618a1 1 0 00-.553-.894L15 7m0 13V7m0 0L9 7" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {isAuthenticated ? "Set your travel preferences" : "Sign in for a personalised feed"}
            </h2>
            <p className="text-sm text-gray-500 mb-6 max-w-xs">
              {isAuthenticated
                ? "Tell us where you want to go and we'll find the best deals personalised for you."
                : "Create a free account and we'll surface the best deals tailored to your travel style."}
            </p>
            <button
              onClick={() =>
                isAuthenticated
                  ? navigate("/onboarding/buyer-preferences")
                  : navigate("/login", { state: { from: "/feed", mode: "signup" } })
              }
              className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              {isAuthenticated ? "Set Preferences" : "Sign up — it's free"}
            </button>
          </div>
        )}

        {/* Error */}
        {error && !noPrefs && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 mb-6">
            {error}
            <button
              onClick={() => { setPage(0); void loadFeeds(0, true); }}
              className="ml-3 text-red-700 font-medium underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && feeds.length === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
                <div className="h-44 bg-gray-200" />
                <div className="p-3.5 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Seller Lead Inbox — rec engine debug view, remove after testing */}
        {(user as any)?.role === "seller" && (
          <div className="mb-8 border border-purple-200 rounded-2xl p-4 bg-purple-50">
            <h2 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
              ⚡ REC ENGINE — Routed Leads
              <span className="text-xs font-normal text-purple-500">(temp debug — remove after testing)</span>
            </h2>
            {sellerLeadsLoading && <p className="text-xs text-purple-400">Loading leads…</p>}
            {!sellerLeadsLoading && sellerLeads.length === 0 && (
              <p className="text-xs text-purple-400">No routed leads yet. Appear after buyers send enquiries.</p>
            )}
            {sellerLeads.map((item: any, i: number) => {
              const lead = item.lead || item;
              return (
                <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-purple-100 mb-2 last:mb-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">
                      {lead.destination || "Unknown destination"}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {lead.enquiry_text || lead.message || "—"}
                    </p>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {lead.pax && <span className="text-[10px] text-gray-400">{lead.pax} pax</span>}
                      {lead.travel_month && <span className="text-[10px] text-gray-400">{lead.travel_month}</span>}
                      {lead.budget && <span className="text-[10px] text-gray-400">₹{lead.budget}</span>}
                      {lead.category && <span className="text-[10px] text-purple-400">{lead.category}</span>}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-[10px] font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">
                      Score {Math.round(item.score ?? 0)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Feed Grid */}
        {feeds.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {feeds.map((item, idx) => (
                <FeedCard
                  key={feedId(item.feed as Record<string, unknown>) || idx}
                  item={item}
                  onVisible={handleVisible}
                  onClick={handleCardClick}
                />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="px-6 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  {loading ? "Loading…" : "Load More"}
                </button>
              </div>
            )}

            {!hasMore && feeds.length > 0 && (
              <p className="text-center text-xs text-gray-400 mt-8">
                You've seen all your personalised deals.{" "}
                <button onClick={() => navigate("/profile")} className="text-blue-500 hover:underline">
                  Update preferences
                </button>{" "}
                to discover more.
              </p>
            )}
          </>
        )}

        {/* Empty after load */}
        {!loading && !noPrefs && feeds.length === 0 && !error && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <p className="text-sm text-gray-500 mb-4">
              No deals match your preferences yet. Sellers are still uploading!
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-5 py-2 border border-gray-300 rounded-xl text-sm hover:bg-gray-50"
            >
              Browse All Deals
            </button>
          </div>
        )}
      </main>

      {/* Detail Modal */}
      {selectedItem && (
        <FeedModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onSave={handleSave}
          onLike={handleLike}
          onEnquiry={handleEnquiry}
        />
      )}
    </div>
  );
};

export default RecommendedFeedPage;
